import PropTypes from 'prop-types';
import {
  Fragment, useCallback, useEffect, useState, useMemo,
} from 'react';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import ArrowRightAlt from '@material-ui/icons/ArrowRightAlt';
import clsx from 'clsx';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { FaTimes } from 'react-icons/fa';
import flightSearchInput from '../../../../helpers/selectors/flightSearchInput';
import {
  FlightFiltersOptions, FlightSearchTypes, InitialFlightFilters, MaximumFlightSegments,
  InitialFormValidations, TravelTypes,
} from '../../../../constants';
import ReturnDateFlightFilter from '../FlightFilterBar/ReturnDateFlightFilter';
import FlightSearchTypesFilter from '../FlightFilterBar/FlightSearchTypesFilter';
import FromFlightFilter from '../FlightFilterBar/FromFlightFilter';
import LeaveDateFlightFilter from '../FlightFilterBar/LeaveDateFlightFilter';
import CabinClassFilter from './CabinClassFilter';
import AirlinesLookup from './AirlinesLookup';
import FlightTimeSlidersInput from './FlightTimeSlidersInput';
import AgentPreferencesFilter from '../FlightFilterBar/AgentPreferencesFilter';
import RateCodePreferencesFilter from '../FlightFilterBar/RateCodePreferencesFilter';
import FlightStops from '../FlightFilterBar/FlightStops';
import ToolTip from '../../../ToolTip';
import { initDirectSellFlightSearchRequest, initFlightSearchRequest, updateFlightSearchObj } from '../../../../store/slices/flight';
import checkIsFlightInputsChanged from '../../../../helpers/checkIsFlightInputsChanged';
import checkIfInBoundFlightInputsChanged from '../../../../helpers/checkIfInBoundFlightInputsChanged';
import { checkDoEnableSelectFlightsBySegment, getTravelPolicyLeadDays } from '../../../../store/slices/user';
import AlliancesLookup from './AlliancesLookup';
import ConnectingAirports from './ConnectingAirports';
import LayoverDurationInput from './LayoverDurationInput';
import DirectFlightDateFilter from '../FlightFilterBar/DirectFlightDateFilter';
import CarrierCodeFilter from '../FlightFilterBar/CarrierCodeFilter';
import FlightNumberInput from '../FlightFilterBar/FlightNumberInput';
import CabinInput from '../FlightFilterBar/CabinInput';
import CheckAvailabilityModal from '../FlightFilterBar/CheckAvailabilityModal';
import ErrorTooltip from '../../../ErrorTooltip';
import validateFlightSearchInputs from '../../../Header/Searchbar/Flight/validateFlightSearchInputs';

const FlightSearchBar = ({ tripId }) => {
  const [toolTip, setToolTip] = useState(false);
  const isTripSearch = !tripId;
  const dispatch = useDispatch();
  const { travelAgency } = useSelector((store) => store.user);
  const doHidePostSearchFilters = travelAgency && travelAgency.settings
    && travelAgency.settings.doHidePostSearchFilters;
  const {
    searchObjRecentSearch: searchObj, breadCrumbsIndex,
    selectedFlightSegments, searchInputs,
  } = useSelector(flightSearchInput(tripId, isTripSearch));
  const isMultiCitySearch = searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY;
  const isRoundTripSearch = searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP;
  const isDirectSellSearch = searchInputs.flightSearchType === FlightSearchTypes.DIRECT_SELL;
  const isOneWaySearch = searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY;
  const [doOpenFirstDatePicker, setDoOpenFirstDatePicker] = useState(false);
  const [doOpenSecondDatePicker, setDoOpenSecondDatePicker] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(undefined);
  const doEnableSelectFlightsBySegment = useSelector(checkDoEnableSelectFlightsBySegment);
  const [formValidations, setFormValidations] = useState(InitialFormValidations);
  const leadDays = useSelector(getTravelPolicyLeadDays(TravelTypes.flight));

  const handleSearch = () => {
    const isValid = validateFlightSearchInputs(setFormValidations,
      leadDays, searchInputs.flightSearchType, searchInputs, true);
    if (!isValid) return;

    if (isDirectSellSearch) {
      dispatch(initDirectSellFlightSearchRequest({ isTripSearch: false, tripId }));
      return;
    }
    const payload = {
      tripId,
      searchObj: {
        page: 1,
        ...InitialFlightFilters,
        hasUserInteractedWithFlightList: false,
        foundTotalFlights: null,
        searchProgress: null,
      },
      airlinesAutocompleteResults: [],
    };
    if (doEnableSelectFlightsBySegment) {
      const inboundFlightSegmentChangeIndex = checkIfInBoundFlightInputsChanged({
        searchInputs,
        searchObject: searchObj,
      });
      const isThereChange = checkIsFlightInputsChanged({
        searchInputs,
        compareBase:
        searchObj,
      });
      if (breadCrumbsIndex === 0 || (breadCrumbsIndex !== 0 && !isThereChange)
              || inboundFlightSegmentChangeIndex <= 0) {
        payload.searchObj.selectedFlightSegments = {};
        payload.searchObj.breadCrumbsIndex = 0;
      } else {
        delete payload.searchObj.selectedFlightSegments;
        delete payload.searchObj.breadCrumbsIndex;
      }
      if (breadCrumbsIndex !== inboundFlightSegmentChangeIndex
              && inboundFlightSegmentChangeIndex > 0 && selectedFlightSegments[breadCrumbsIndex]) {
        payload.searchObj.breadCrumbsIndex = inboundFlightSegmentChangeIndex;
      }
    }
    dispatch(initFlightSearchRequest(payload));
  };

  const handleOpenFirstDatePicker = useCallback((val) => {
    setDoOpenFirstDatePicker(val);
    if (!val) {
      setTimeout(() => { setDoOpenSecondDatePicker(true); }, 50);
    }
  }, []);

  const handleOpenMultiCityDatePicker = useCallback((isOpen, index) => {
    setDatePickerOpen(isOpen ? index : undefined);
    if (!isOpen && index < (searchInputs.flightSegments || []).length - 1) {
      setDatePickerOpen(index + 1);
    }
  }, [searchInputs.flightSegments]);

  const handleResetValidation = () => {
    setFormValidations(InitialFormValidations);
  };

  useEffect(() => {
    if (doOpenFirstDatePicker) setDoOpenSecondDatePicker(false);
  }, [doOpenFirstDatePicker]);

  const fromElement = (fromIndex) => (FlightFiltersOptions.map((item, index) => (
    <Fragment key={`${item.type}-${fromIndex.toString()}-${index.toString()}`}>
      <ErrorTooltip
        title={formValidations.flightSegments[fromIndex]?.[item.selectedItemName] || ''}
        open={!_.isEmpty(formValidations.flightSegments[fromIndex]?.[item.selectedItemName])}
        arrow
      >
        <div className={isMultiCitySearch ? 'w-1/3 max-w-1/3 min-w-1/3' : 'm-w-full'}>
          <FromFlightFilter
            key={tripId + index.toString()}
            tripId={tripId}
            type={item.type}
            autocompleteType={item.autocompleteType}
            autocompleteResultsType={item.autocompleteResultsType}
            trackEventName={item.trackEventName}
            filterButtonMaxWidth="w-full"
            selectedItemName={item.selectedItemName}
            index={fromIndex}
          />
        </div>
      </ErrorTooltip>
      {index === 0 && (isRoundTripSearch ? <CompareArrowsIcon className="m-w-full" /> : <ArrowRightAlt className="w-full" />)}
    </Fragment>
  )));

  const otherInputs = (
    <div className="mt-2 flex flex-wrap gap-2">
      <FlightStops tripId={tripId} variant="outline" />
      <CabinClassFilter tripId={tripId} variant="outline" filterMaxWidthClass="max-w-96" />
      <ConnectingAirports tripId={tripId} variant="outline" />
      <AirlinesLookup tripId={tripId} variant="outline" />
      <FlightTimeSlidersInput tripId={tripId} variant="outline" />
      <AgentPreferencesFilter tripId={tripId} variant="outline" />
      <RateCodePreferencesFilter tripId={tripId} variant="outline" />
      <LayoverDurationInput tripId={tripId} variant="outline" />
      <AlliancesLookup tripId={tripId} variant="outline" />
    </div>
  );

  const isAddFlightSegmentDisabled = useMemo(() => searchInputs.flightSegments
  && searchInputs.flightSegments.length === MaximumFlightSegments,
  [searchInputs.flightSegments]);

  const handleAddAnotherFlight = (event) => {
    if (event) event.preventDefault();
    if (searchInputs.flightSegments.length < MaximumFlightSegments) {
      const payload = {
        tripId,
        searchObj: {
          flightSegments: [...searchInputs.flightSegments, {}],
        },
      };
      dispatch(updateFlightSearchObj(payload));
    }
  };

  const handleClearFlightChoice = (event) => {
    if (event) event.preventDefault();
    const payload = {
      tripId,
      searchObj: {
        flightSegments: isDirectSellSearch ? [{}] : [{}, {}],
      },
    };
    dispatch(updateFlightSearchObj(payload));
  };

  const handleRemoveFlightSegmentRow = (event, rowIndex) => {
    if (event) event.preventDefault();
    const minSegments = isDirectSellSearch ? 1 : 2;
    if ((isMultiCitySearch || isDirectSellSearch)
      && searchInputs.flightSegments.length > minSegments) {
      const currentFlightSegments = [...searchInputs.flightSegments];
      _.remove(currentFlightSegments, (value, index) => index === rowIndex);
      const payload = {
        tripId,
        searchObj: {
          flightSegments: currentFlightSegments,
        },
      };
      dispatch(updateFlightSearchObj(payload));
    }
  };

  useEffect(() => {
    if (!doHidePostSearchFilters) return;
    const areFiltersDifferent = checkIsFlightInputsChanged({
      searchInputs,
      compareBase: searchObj,
    });
    setToolTip(areFiltersDifferent);
  }, [searchObj, searchInputs, doHidePostSearchFilters]);

  const handleOnClose = () => {
    setToolTip(false);
  };

  const checkDoOpenDatePicker = useCallback((currentIndex) => (isRoundTripSearch
    || isOneWaySearch ? doOpenFirstDatePicker
    : datePickerOpen === currentIndex),
  [datePickerOpen, doOpenFirstDatePicker, isRoundTripSearch, isOneWaySearch]);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [availabilitySegment, setAvailabilitySegment] = useState(0);

  const handleCheckAvailabilityClick = (segmentIndex) => {
    setAvailabilitySegment(segmentIndex);
    setIsAvailabilityModalOpen((prev) => !prev);
  };
  const renderDirectSellFields = (segmentIndex) => {
    // const currentSegment = searchInputs?.flightSegments[segmentIndex] || {};
    const validation = formValidations.flightSegments[segmentIndex] || {};
    const prevSegment = searchInputs?.flightSegments[segmentIndex - 1];
    return (
      <div className="flex flex-wrap gap-2">
        <ErrorTooltip
          title={validation.departureDate || ''}
          open={!_.isEmpty(validation.departureDate)}
          arrow
        >
          <div className="w-48">
            <DirectFlightDateFilter
              tripId={tripId}
              dateFilterType="departureDate"
              currentIndex={segmentIndex}
              label="Departure date"
              variant="outline"
              minDate={prevSegment?.departureDate}
            />
          </div>
        </ErrorTooltip>
        {/* <div className="w-48">
          <DirectFlightDateFilter
            tripId={tripId}
            dateFilterType="arrivalDate"
            currentIndex={segmentIndex}
            label="Arrival date"
            variant="outline"
            minDate={currentSegment?.departureDate}
          />
        </div> */}
        <ErrorTooltip
          title={validation.carrierCode || ''}
          open={!_.isEmpty(validation.carrierCode)}
          arrow
        >
          <div className="w-48">
            <CarrierCodeFilter
              variant="outline"
              currentIndex={segmentIndex}
              tripId={tripId}
            />
          </div>
        </ErrorTooltip>
        <ErrorTooltip
          title={validation.flightNumber || ''}
          open={!_.isEmpty(validation.flightNumber)}
          arrow
        >
          <div className="w-48">
            <FlightNumberInput currentIndex={segmentIndex} tripId={tripId} />
          </div>
        </ErrorTooltip>
        <ErrorTooltip
          title={validation.cabin || ''}
          open={!_.isEmpty(validation.cabin)}
          arrow
        >
          <div className="w-48">
            <CabinInput currentIndex={segmentIndex} tripId={tripId} />
          </div>
        </ErrorTooltip>
        <div className="col-span-12 flex flex-row justify-end items-center">
          <button
            onClick={() => handleCheckAvailabilityClick(segmentIndex)}
            type="button"
            className="text-sm underline px-2"
          >
            Check Availability
          </button>
        </div>
      </div>
    );
  };

  return (
    <div onBlur={handleResetValidation}>
      <FlightSearchTypesFilter tripId={tripId} />
      <div className="block sm:flex flex-auto gap-2 mb-2 w-full">
        <div className="text-theme pb-2 w-full">
          {searchInputs.flightSegments
            && searchInputs.flightSegments.map((segment, segmentIndex) => {
              if (isRoundTripSearch
                && segmentIndex === 1) {
                return null;
              }
              return (
                <div key={`${segmentIndex.toString()}`} className={clsx('flex gap-2 items-center mb-2 flex-wrap', { 'mb-8': isDirectSellSearch })}>
                  {fromElement(segmentIndex)}
                  {isDirectSellSearch && (
                    renderDirectSellFields(segmentIndex)
                  )}
                  {!isDirectSellSearch && (
                  <ErrorTooltip
                    title="Departure date is required"
                    open={!_.isEmpty(formValidations.flightSegments[segmentIndex]?.leaveDate)}
                    arrow
                  >
                    <div>
                      <LeaveDateFlightFilter
                        flightRowIndex={segmentIndex}
                        doOpenDatePicker={checkDoOpenDatePicker(segmentIndex)}
                        setDoOpenDatePicker={searchInputs.flightSearchType
                          === FlightSearchTypes.MULTI_CITY
                          ? handleOpenMultiCityDatePicker : handleOpenFirstDatePicker}
                        tripId={tripId}
                        filterButtonMaxWidth={isMultiCitySearch ? `${isTripSearch ? 'w-full' : 'w-auto'}` : 'm-w-full'}
                      />
                    </div>
                  </ErrorTooltip>
                  )}
                  {isRoundTripSearch && (
                  <ErrorTooltip
                    title={formValidations.flightSegments[1]?.returnDate || ''}
                    open={!_.isEmpty(formValidations.flightSegments[1]?.returnDate)}
                    arrow
                  >
                    <div>
                      <ReturnDateFlightFilter
                        doOpenDatePicker={doOpenSecondDatePicker}
                        setDoOpenDatePicker={setDoOpenSecondDatePicker}
                        tripId={tripId}
                        filterButtonMaxWidth={isMultiCitySearch ? `${isTripSearch ? 'w-full' : 'w-auto'}` : 'm-w-full'}
                        label="Return date"
                        flightRowIndex={1}
                      />
                    </div>
                  </ErrorTooltip>
                  )}
                  {((isMultiCitySearch && segmentIndex > 1)
                  || (isDirectSellSearch && segmentIndex !== 0)) && (
                  <div className="flex">
                    <button
                      type="button"
                      onClick={(e) => handleRemoveFlightSegmentRow(e,
                        segmentIndex)}
                      className="items-center ml-4 underline focus:outline-none"
                    >
                      {isDirectSellSearch ? <FaTimes /> : 'Remove'}
                    </button>
                  </div>
                  )}
                </div>
              );
            })}
          {!isDirectSellSearch && otherInputs}
          {(isMultiCitySearch || isDirectSellSearch) && (
          <div className="flex gap-6 py-2">
            <button type="button" disabled={isAddFlightSegmentDisabled} onClick={handleAddAnotherFlight} className={clsx({ 'opacity-50 cursor-auto': isAddFlightSegmentDisabled })}>+ Add another flight</button>
            <button type="button" onClick={handleClearFlightChoice}> Clear all </button>
          </div>
          )}
        </div>
        <div>
          <ToolTip zIndex={1} onClose={handleOnClose} title="Please click Search to apply the newly changed parameters" open={toolTip}>
            <button
              type="submit"
              className="px-8 min-h-12 bg-searchButton-0 text-searchbtn-0 text-base font-bold w-max rounded-2xl"
              onClick={handleSearch}
            >
              Search
            </button>
          </ToolTip>
        </div>
      </div>
      {isDirectSellSearch && (
      <CheckAvailabilityModal
        tripId={tripId}
        doShow={isAvailabilityModalOpen}
        onClose={() => setIsAvailabilityModalOpen((prev) => !prev)}
        currentIndex={availabilitySegment}
      />
      )}
    </div>
  );
};

FlightSearchBar.propTypes = {
  tripId: PropTypes.string.isRequired,
};

export default FlightSearchBar;
